/*
 * Copyright (c) 2023 Powerledger Pty Ltd. All Rights Reserved.
 */

import {
  BoxExportSVG,
  CoinStackSvg,
  HandBagSVG,
  HandSVG,
  InstituteSVG,
  WalletNakedSVG,
} from "@powerledger/ui-component-lib";
import { useCallback, useMemo, useState } from "react";
import { useLocation } from "react-router";

import { useSidebarOpen } from "@/app/hooks/use-sidebar-open";
import { grantAccess } from "@/app/lib/role-based-access-control";
import { routes } from "@/app/navigation/routes";
import { MessageStatus, useInboxMessagesQuery } from "@/app/types/generated/graphql";
import { LineGraphSVG, ListTickSVG } from "@/assets/icons";

import { UseLayout } from "./layout.types";

export const getIcons = (): Record<string, any> => ({
  BoxExport: <BoxExportSVG />,
  Hand: <HandSVG />,
  WalletNaked: <WalletNakedSVG />,
  Institute: <InstituteSVG />,
  HandBag: <HandBagSVG />,
  CoinStack: <CoinStackSvg />,
  ListTick: <ListTickSVG />,
  LineGraph: <LineGraphSVG />,
});
export const useLayout: UseLayout = ({ userRoles }) => {
  const location = useLocation();
  const [expandedRoutes, setExpandedRoutes] = useState<string[]>([]);

  const { data: inboxMessagesData } = useInboxMessagesQuery({ fetchPolicy: "network-only" });

  const { sidebarOpen, toggleSidebarOpen } = useSidebarOpen();

  const currentRoute = useMemo(
    () =>
      routes.find(({ path, secured }) => {
        return secured && location.pathname?.slice(1).includes(path.slice(1));
      }),
    [location],
  );

  const handleToggleRoute = useCallback(
    (path: string) =>
      expandedRoutes?.includes(path)
        ? setExpandedRoutes((state) => state.filter((route) => route !== path))
        : setExpandedRoutes((state) => [...state, path]),
    [expandedRoutes],
  );

  const layoutPropsFromNav = location.pathname === currentRoute?.path ? currentRoute.nav?.props : {};

  const currentSubRoute = useMemo(() => {
    const path = location?.pathname.split("/") || [];
    const endRoute = path[path.length - 1];
    return currentRoute?.children?.find(({ path }) => endRoute === path.substring(1));
  }, [location, currentRoute]);

  const userInitials = "ME";

  /**
   * Aggregate Logic Put when required while filtering routes
   *  const isAggregatee = false;
   *  (!isAggregatee || (isAggregatee && ['Account', 'Generation'].includes(navTitle)))
   */
  const filteredRoutes = routes.filter(({ nav, guards }) => !!nav && grantAccess(userRoles, guards?.roles || []));

  const headerTitle =
    currentRoute?.path === location.pathname
      ? currentRoute?.nav?.displayName ?? ""
      : currentSubRoute
      ? currentSubRoute?.nav?.displayName ?? ""
      : "";

  const unreadMessageCount = inboxMessagesData?.messages?.filter((m) => m?.status === MessageStatus.Unread).length;

  return {
    currentRoute,
    sidebarOpen,
    filteredRoutes,
    unreadMessageCount,
    updateSidebarState: toggleSidebarOpen,
    userInitials,
    expandedRoutes,
    currentSubRoute,
    headerTitle,
    layoutPropsFromNav,
    handleToggleRoute,
  };
};
